import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useResponsive } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { useBreadcrumbs } from '@/modules/core/hooks';
import {
  useCreatePatientQuery,
  useCreateProviderQuery,
  useGetAiSuggestedServicesQuery,
  useUpdateProviderQuery,
} from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { ClaimSummaryField, UseMyMoneyOption, UseMyMoneySummaryTitle } from '@/modules/UseMyMoney/UseMyMoney.types';
import AccountsDetailsContainer from '@/modules/UseMyMoney/widgets/AccountDetails/AccountDetails.container';
import { useClaimSummarySetupFields } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';
import DocumentPreview from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/DocumentUploadCard/DocumentPreview';
import DocumentUploadCard from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/DocumentUploadCard/DocumentUploadCard';

import {
  AmountFieldSetup,
  DateOfServiceSetup,
  FieldSetupFooter,
  FieldSetupHeader,
  ProviderFieldSetup,
  ServiceDescriptionFieldSetup,
ServiceForFieldSetup,
} from './components';

interface Props {
  activeField?: ClaimSummaryField;
  setActiveField: (activeField?: ClaimSummaryField) => void;
  useMyMoneyOption?: UseMyMoneyOption;
  hasAvailableAccounts?: boolean;
  onUploadFile?: () => void;
  onDeleteFile?: (fileId: string) => Promise<unknown>;
  onDeleteDocument?: (documentId: string) => Promise<unknown>;
  isUploadNewFilePopupVisible?: boolean;
}

const FieldSetupContainer: React.FC<Props> = ({
  activeField,
  setActiveField,
  useMyMoneyOption,
  hasAvailableAccounts,
  onUploadFile,
  onDeleteFile,
  onDeleteDocument,
  isUploadNewFilePopupVisible,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { fields } = useClaimSummarySetupFields({ hasValidDate: hasAvailableAccounts });
  const { createProvider } = useCreateProviderQuery();
  const { updateProvider } = useUpdateProviderQuery();
  const { createPatient } = useCreatePatientQuery();
  const { values } = useMyMoneyFormStore();
  const [isPreviewDocumentPopupOpen, setIsPreviewDocumentPopupOpen] = useState(false);

  const { aiSuggestedAccountOrdersArrays, isAiSuggestedAccountOrdersLoading } = useGetAiSuggestedServicesQuery({
    dateOfService: values?.dateOfService,
    dependentId: values?.patientId,
    searchString: values?.aiServicesSearchString,
  });
  
  const handleSetActiveField = useCallback((field: string) => {
    setActiveField(field as ClaimSummaryField);
  }, [setActiveField]);

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.USE_MY_MONEY,
        title: t('Use My Money'),
        value: ROUTES.USE_MY_MONEY,
      },
      {
        key: ROUTES.USE_MY_MONEY_SUMMARY,
        title: useMyMoneyOption ? t(UseMyMoneySummaryTitle?.[useMyMoneyOption]) : t('Claim Summary'),
        value: ROUTES.USE_MY_MONEY_SUMMARY,
      },
      {
        key: ROUTES.USE_MY_MONEY_SETUP_FIELDS,
        title: ' ',
        value: ROUTES.USE_MY_MONEY_SETUP_FIELDS,
      },
    ],
    true,
    { backBtnTitle: useMyMoneyOption ? `${t('Back to')} ${t(UseMyMoneySummaryTitle?.[useMyMoneyOption]) }`: t('Claim Summary') }
  );

  const handleOpenPreviewPopup = useCallback(() => {
    setIsPreviewDocumentPopupOpen(true);
  }, []);

  const handleClosePreviewPopup = useCallback(() => {
    setIsPreviewDocumentPopupOpen(false);
  }, []);

  return (
    <Box
      data-testId="field-setup-container"
      flex="grow"
    >
      {isMobile ? null : (
        <FieldSetupHeader
          activeField={activeField}
          setActiveField={handleSetActiveField}
          fields={fields}
        />
      )}

      <Box margin={{ top: 'spacing24' }} direction="row" gap="spacing24">
        <Box width="100%">
          {activeField === ClaimSummaryField.AMOUNT && <AmountFieldSetup />}
          {activeField === ClaimSummaryField.PROVIDER && (
            <ProviderFieldSetup
              createProvider={createProvider}
              updateProvider={updateProvider}
            />
          )}
          {activeField === ClaimSummaryField.SERVICE_FOR && (
            <ServiceForFieldSetup
              createPatient={createPatient}
            />
          )}
          {activeField === ClaimSummaryField.DATE_OF_SERVICE && (
            <DateOfServiceSetup hasAvailableAccounts={hasAvailableAccounts} />
          )}
          {activeField === ClaimSummaryField.SERVICE_DESCRIPTION && (
            <ServiceDescriptionFieldSetup
              aiSuggestedAccountOrdersArrays={aiSuggestedAccountOrdersArrays}
              isAiSuggestedAccountOrdersLoading={isAiSuggestedAccountOrdersLoading}
              hasAvailableAccounts={hasAvailableAccounts}
            />
          )}
        </Box>
        
        {isMobile ? null : (
          <Box width={{ width: '100%', max: '300px' }}>
            <Box
              direction="column"
              gap="spacing24"
              style={{
                opacity: isPreviewDocumentPopupOpen ? 0 : 1,
                pointerEvents: isPreviewDocumentPopupOpen ? 'none' : 'auto',
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <DocumentUploadCard
                onOpenPreview={handleOpenPreviewPopup}
                onUploadFile={onUploadFile}
              />
              <AccountsDetailsContainer />
            </Box>

            {isPreviewDocumentPopupOpen && (
              <DocumentPreview
                onClose={handleClosePreviewPopup}
                onUploadFile={onUploadFile}
                onDeleteFile={onDeleteFile}
                onDeleteDocument={onDeleteDocument}
                isClickOutsideBlocked={isUploadNewFilePopupVisible}
              />
            )}
          </Box>
        )}
      </Box>

      <FieldSetupFooter
        margin={{ top: 'auto' }}
        activeField={activeField}
        setActiveField={setActiveField}
        fields={fields}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default FieldSetupContainer;
